/**
 * Utility functions for API calls and error handling
 */

/**
 * Retry an API call with exponential backoff
 * @param {Function} apiCall - The API call function to retry
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} baseDelay - Base delay in milliseconds between retries
 * @returns {Promise} - Resolves with API response or rejects with error
 */
export const retryApiCall = async (apiCall, maxRetries = 3, baseDelay = 1000) => {
  let lastError;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await Promise.race([
        apiCall(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 30000)
        )
      ]);
      return response;
    } catch (error) {
      lastError = error;
      
      // Don't retry if it's a client error (4xx)
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      
      // Add some randomness to prevent thundering herd
      const jitter = Math.random() * 1000;
      
      await new Promise(resolve => setTimeout(resolve, delay + jitter));
    }
  }

  throw lastError;
};

/**
 * Handle API errors consistently across the application
 * @param {Error} error - The error object
 * @param {string} fallbackMessage - Fallback message if error details aren't available
 * @returns {Object} - Normalized error object
 */
export const handleApiError = (error, fallbackMessage = 'An error occurred') => {
  // Network or timeout errors
  if (!error.response) {
    return {
      message: 'Network error. Please check your connection.',
      status: 'error',
      details: error.message
    };
  }

  // API errors with response
  const status = error.response.status;

  switch (status) {
    case 400:
      return {
        message: 'Invalid request. Please check your input.',
        status: 'error',
        details: error.response.data?.message || fallbackMessage
      };
    case 401:
      return {
        message: 'Authentication required. Please log in again.',
        status: 'error',
        details: 'Session expired or invalid'
      };
    case 403:
      return {
        message: 'Access denied. You don\'t have permission for this action.',
        status: 'error',
        details: error.response.data?.message || 'Forbidden'
      };
    case 404:
      return {
        message: 'Resource not found.',
        status: 'error',
        details: error.response.data?.message || 'Not found'
      };
    case 429:
      return {
        message: 'Too many requests. Please try again later.',
        status: 'error',
        details: 'Rate limit exceeded'
      };
    default:
      return {
        message: status >= 500 
          ? 'Server error. Please try again later.'
          : fallbackMessage,
        status: 'error',
        details: error.response.data?.message || error.message
      };
  }
};

/**
 * Check if an error is a network timeout
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a timeout error
 */
export const isTimeoutError = (error) => {
  return (
    error.message === 'Request timeout' ||
    error.code === 'ECONNABORTED' ||
    (error.response && error.response.status === 408)
  );
};